import React, { useState, useEffect, useCallback } from 'react';
import { Box, TextField, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

/** @deprecated Prefer CSS ellipsis on tags; kept for any external imports. */
export const filterTagsMicroLabel = (s) => {
    if (s == null || s === '') return '';
    const str = String(s);
    if (str.length <= 4) return str;
    return `${str.slice(0, 4)}…`;
};

const isFullWidth = (textMaxWidth) => textMaxWidth === 'full' || textMaxWidth === Infinity;

const FilterValueTag = ({ value: val, onRemove, disabled, textMaxWidth }) => {
    const full = isFullWidth(textMaxWidth);
    return (
        <Box
            title={val}
            sx={{
                display: 'inline-flex',
                alignItems: 'stretch',
                maxWidth: full ? '100%' : textMaxWidth + 40,
                flexShrink: 0,
                alignSelf: 'flex-start',
                borderRadius: '4px',
                border: 1,
                borderColor: 'divider',
                bgcolor: 'action.hover',
                overflow: 'hidden',
            }}
        >
            <Typography
                component="span"
                variant="body2"
                sx={{
                    pl: 0.75,
                    pr: 0.5,
                    py: '4px',
                    alignSelf: 'center',
                    maxWidth: full ? '100%' : textMaxWidth,
                    fontSize: '0.8125rem',
                    lineHeight: 1.25,
                    color: 'text.primary',
                    ...(full
                        ? { whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'anywhere' }
                        : {
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                          }),
                }}
            >
                {val}
            </Typography>
            <IconButton
                size="small"
                disabled={disabled}
                onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    onRemove();
                }}
                sx={{
                    borderRadius: 0,
                    flexShrink: 0,
                    width: 28,
                    height: 'auto',
                    minWidth: 28,
                    pl: '2px',
                    pr: '4px',
                    alignSelf: 'stretch',
                    color: 'text.secondary',
                    '&:hover': { bgcolor: 'action.selected' },
                }}
                aria-label="Remove value"
            >
                <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
        </Box>
    );
};

const FilterTagsInput = ({
    values = [],
    onValuesChange,
    disabled,
    onDraftChange,
    endAdornment,
    textFieldProps = {},
    panelStyle,
    /** @type {'full' | number} Full label in editor / panel; omit defaults to 'full'. */
    tagTextMaxWidth: tagTextMaxWidthProp = 'full',
}) => {
    const [draft, setDraft] = useState('');
    const isPanel = Boolean(panelStyle);
    const tagTextMaxWidth = tagTextMaxWidthProp ?? 'full';

    useEffect(() => {
        onDraftChange?.(draft);
    }, [draft, onDraftChange]);

    const commitDraft = useCallback(() => {
        const next = draft.trim();
        if (!next) return;
        onValuesChange([...values, next]);
        setDraft('');
    }, [draft, values, onValuesChange]);

    const handleKeyDown = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            commitDraft();
        }
    };

    const handleChange = (event) => {
        const v = event.target.value;
        if (v.includes(',')) {
            const parts = v.split(',');
            const completed = parts
                .slice(0, -1)
                .map((p) => p.trim())
                .filter(Boolean);
            const rest = parts[parts.length - 1] ?? '';
            if (completed.length) {
                onValuesChange([...values, ...completed]);
            }
            setDraft(rest);
            return;
        }
        setDraft(v);
    };

    const removeAt = (index) => {
        onValuesChange(values.filter((_, i) => i !== index));
    };

    const inner = (
        <>
            {values.map((val, i) => (
                <FilterValueTag
                    key={`${i}-${val}`}
                    value={val}
                    textMaxWidth={tagTextMaxWidth}
                    onRemove={() => removeAt(i)}
                    disabled={disabled}
                />
            ))}
            <TextField
                variant="standard"
                size="small"
                value={draft}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                disabled={disabled}
                sx={{ flex: '1 1 120px', minWidth: 80 }}
                {...textFieldProps}
                InputProps={{
                    endAdornment,
                    ...textFieldProps.InputProps,
                }}
            />
        </>
    );

    const containerSx = {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: isPanel ? '6px' : '8px',
        width: '100%',
        boxSizing: 'border-box',
        ...(isPanel ? {} : { py: '4px' }),
    };

    if (panelStyle) {
        return (
            <Box
                sx={{
                    ...containerSx,
                    minHeight: 36,
                    ...panelStyle,
                }}
            >
                {inner}
            </Box>
        );
    }

    return <Box sx={containerSx}>{inner}</Box>;
};

/** Pill used in the column header strip (single row, overflow clip); not used in the edit popover. */
export const InFilterCompactChip = ({ label }) => (
    <Box
        component="span"
        title={label}
        sx={{
            flexShrink: 0,
            display: 'inline-block',
            maxWidth: 40,
            px: 0.5,
            py: '3px',
            verticalAlign: 'middle',
            borderRadius: '4px',
            border: 1,
            borderColor: 'divider',
            bgcolor: 'action.hover',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontSize: '0.75rem',
            lineHeight: 1.2,
            color: 'text.primary',
            boxSizing: 'border-box',
        }}
    >
        {label}
    </Box>
);

export default FilterTagsInput;
